import { createContext, useState } from "react";
import sfAuth from "./SFAuth";
import useInterval from "./useInterval";
import Config from "~/config";

const defaultAuthContext: AuthContext = {
	loadUserSession: () => Promise.resolve(),
	sessionChecked: false,
	loggedIn: false,
	currentUser: null,
	userIsInSilverFern: false,
	company: null,
	profiles: {
		mobileApps: null,
		inventory: null,
	},
	// eslint-disable-next-line
	redirectToLogin: () => {},
	// eslint-disable-next-line
	redirectToLogout: () => {},
	performCodeExchange: (code: string) => Promise.resolve(),
	error: null,
};

export const AuthContext = createContext<AuthContext>(defaultAuthContext);

interface AuthProviderProps {
	children: React.ReactNode;
}

const TOKEN_DURATION_SECONDS = Config.tokenDuration;

function AuthProvider({ children }: AuthProviderProps) {
	const [loggedIn, setLoggedIn] = useState(false);
	const [sessionChecked, setSessionChecked] = useState(false);
	const [currentUser, setCurrentUser] = useState<Maybe<SFUser>>(null);
	const [company, setCompany] = useState<Maybe<SFCompany>>(null);
	const [mobileAppProfiles, setMobileAppProfiles] = useState<Maybe<MobileAppsProfile[]>>(null);
	const [authError, setAuthError] = useState<Maybe<string>>(null);

	/** Load user's auth session, updating the AuthProvider context with the result.
	 * @param redirect Whether to redirect to login if not logged in
	 * @returns `Promise<void>` for whether the user is logged in
	 */
	async function loadUserSession(redirect: boolean = true): Promise<void> {
		if (sessionChecked) {
			return;
		}

		try {
			const hasSession = await sfAuth.checkSession();
			// // Utility route to remain on page even without a session; helpful for
			// // checking for a session without getting redirected
			// if (window.location.pathname.startsWith('/no-redirect')) {
			//   return;
			// }
			if (!hasSession) {
				if (redirect) {
					await sfAuth.redirectToLogin();
				}
				setLoggedIn(false);
			} else {
				setLoggedIn(true);
				await fetchUserInfo();
			}
		} catch (e) {
			console.log("Error checking session: ", e);
			setLoggedIn(false);
		} finally {
			setSessionChecked(true);
		}
	}

	/** Check session/renew access token periodically */
	useInterval(() => {
		(async () => {
			try {
				const hasSession = await sfAuth.checkSession();
				if (!hasSession) {
					await sfAuth.redirectToLogin();
				}
			} catch (e) {
				console.log("Error periodically checking session: ", e);
				await sfAuth.redirectToLogin();
			}
		})();
	}, (TOKEN_DURATION_SECONDS * 1000) / 10);

	let userIsInSilverFern = false;
	const abbreviation = company?.abbreviation;
	if (abbreviation) {
		userIsInSilverFern = ["sfg", "sfd"].indexOf(company.abbreviation.toLowerCase()) > -1;
	}

	async function fetchUserInfo() {
		try {
			const user = await sfAuth.getCurrentUser();
			const company = await sfAuth.getCompany();
			const mobileAppProfiles = await sfAuth.getMobileAppsProfiles();

			setCurrentUser(user);
			setCompany(company);
			setMobileAppProfiles(mobileAppProfiles);
		} catch (e) {
			console.log("Error loading user info: ", e);
			setAuthError(`${e}`);
		}
	}

	/** Perform OAuth Authorization Code Flow code exchange with the provided `code` */
	async function performCodeExchange(code: string): Promise<void> {
		await sfAuth.exchangePKCECodeForTokens(code);
		await fetchUserInfo();
		setLoggedIn(true);
	}

	const contextValue: AuthContext = {
		loadUserSession,
		loggedIn,
		sessionChecked,
		currentUser: currentUser,
		userIsInSilverFern,
		company: company,
		profiles: {
			mobileApps: mobileAppProfiles,
			inventory: null,
		},
		redirectToLogin: sfAuth.redirectToLogin,
		redirectToLogout: sfAuth.redirectToLogout,
		performCodeExchange,
		error: authError,
	};

	return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
